/**
 * The single canonical membership/classification for "which referee entry point a given
 * command/intent routes to". **Every site that needs this classification must import it from
 * here** — `backend/src/refereeDifferential.test.ts`'s `adjudicate` and
 * `packages/ai/src/directRefereeHandle.ts`'s dispatch both do. A hand-copied `Set` in either
 * one silently goes stale the moment a twelfth `refereeCommand`-hooked command or an eighth
 * `GenericResolutionIntent` member lands, and for `directRefereeHandle.ts` that is a silent
 * **adjudication divergence**: the new op routes to a plain `applyCommand` instead of
 * `refereeCommand`/`refereeIntent`, skipping its hook chain.
 *
 * **The room is the conceptual origin but holds no literal copy.** `encounterRoom.ts`'s
 * `handle` dispatches the hooked commands as individual
 * `if (command.type === "…") return this.applyHookedCommand(…)` branches (falling through to a
 * plain `this.applyCommand(…)`) — never a `Set`/array membership check — and
 * `resolveIntentAfterReveal` reaches the generic `refereeIntent` call by TS control-flow
 * narrowing (every bespoke intent, and `request_move`, is routed off in an earlier branch).
 * This file is that room-side classification made literal and importable.
 *
 * **Honesty over the real union types.** `HOOKED_COMMAND_TYPES`/`GENERIC_INTENT_TYPES` are typed
 * against the actual `RefereeHookedCommand`/`GenericResolutionIntent` member unions via a
 * `Record<…, true>` (below) — a member added to, removed from, or typo'd against either type is
 * a compile error here, not silent drift. `dispatchClassification.exhaustive.test.ts` goes one
 * step further and classifies the FULL `EncounterLeafCommand`/`EncounterIntent` unions (every
 * member, not just these two curated subsets) via an exhaustive, default-free `switch`, so a
 * brand-new command or intent type forces a conscious "hooked/plain" or "generic/move/bespoke"
 * classification the moment it's added anywhere in `@lancer-vtt/domain`/`@lancer-vtt/shared`.
 */
import type { RefereeHookedCommand } from "./refereeCommand";
import type { GenericResolutionIntent } from "./refereeIntent";

type HookedCommandType = RefereeHookedCommand["type"];
type GenericIntentType = GenericResolutionIntent["type"];

// A `Record<K, true>` keyed on the literal union — rather than a bare array/Set literal — is
// what makes a missing OR stale member a compile error: TS requires every key of the type
// present (a member added to `RefereeHookedCommand`/`GenericResolutionIntent` upstream and not
// mirrored here fails to compile) and rejects any key outside it (a typo, or a member removed
// upstream and left stale here, both fail to compile). `Object.keys(...)` below then gives the
// runtime `Set` with the same guarantee.
const HOOKED_COMMAND_TYPE_RECORD: Record<HookedCommandType, true> = {
  activate_unit: true,
  end_turn: true,
  end_round: true,
  move_token: true,
  teleport_token: true,
  place_token: true,
  boost: true,
  prepare: true,
  set_door_state: true,
  // Hooked NOT for a followup chain (it has none) but because `refereeCommand` is the one
  // function both `encounterRoom.handle` and `packages/ai`'s `directRefereeHandle` call, so a
  // reserved-name gate placed there walls the AI funnel for free. A room-side gate would leave
  // it open.
  grant_ruling: true,
  // Hooked for the SAME reason as `grant_ruling`: a PRE-apply gate, not a followup chain.
  // The room's own Initiative fold pushes this as a referee PLAN SIBLING,
  // applied via `applyDetached`, so it never passes through `handle`'s ladder — hooking it
  // therefore gates the GM's direct send and leaves the system-issued path untouched.
  grant_action_allowance: true,
  // Hooked for the source-gone status expiry: a removed source's turn-keyed statuses end now.
  remove_combatant: true,
  // Hooked to plant Bonded's `Bondmate` status when a deploy fields a roster Bondmate pair.
  add_combatant: true,
  // Hooked to fire `on_action_taken` for `"stand_up"` (Sea Legs: "when your mech clears Prone").
  stand_up: true,
  // Hooked so the status-tie sweep runs: a removed Lock On takes Markerlit with it.
  remove_status: true,
  // Hooked so a GM marking a unit Destroyed by hand raises `source_gone` (`event-engine-lifecycle` 3.4).
  apply_status: true,
};

const GENERIC_INTENT_TYPE_RECORD: Record<GenericIntentType, true> = {
  request_attack: true,
  request_barrage: true,
  request_activate: true,
  request_stabilize: true,
  request_overcharge: true,
  request_full_tech: true,
};

/**
 * The `refereeCommand`-hooked command types (`backend/src/encounterRoomCore.ts`'s `handle`): the
 * chain-carriers (the three turn-lifecycle commands, the movement family, the misc single-hook
 * commands `boost`/`prepare`/`set_door_state`/`stand_up`, `remove_combatant`'s and a Destroyed
 * `apply_status`'s `source_gone`, `remove_status`'s tie sweep, and `add_combatant`'s Bondmate plant,
 * which the room takes only for a deploy that owes one) plus the two gate-only members,
 * `grant_ruling` and `grant_action_allowance`, hooked for their PRE-apply gates rather than for a
 * followup chain (neither has one).
 * The ONE exported source of truth for `HOOKED_COMMAND_TYPES.has(command.type)`-shaped checks.
 * Typed `ReadonlySet<string>` (rather than `ReadonlySet<HookedCommandType>`) so callers can
 * `.has()` the full, wider `EncounterCommand["type"]` a real dispatch site narrows over — the
 * compile-time honesty guarantee lives in `HOOKED_COMMAND_TYPE_RECORD` above (this Set's
 * construction), not in the membership-check call site's argument type.
 */
export const HOOKED_COMMAND_TYPES: ReadonlySet<string> = new Set(
  Object.keys(HOOKED_COMMAND_TYPE_RECORD) as HookedCommandType[],
);

/**
 * The six `GenericResolutionIntent` member types `refereeIntent` composes
 * (`backend/src/encounterRoom.ts`'s `resolveIntentAfterReveal` generic tail, reached after
 * `request_move` and every bespoke intent is routed off). The ONE exported source of truth for
 * `GENERIC_INTENT_TYPES.has(intent.type)`-shaped checks. Typed `ReadonlySet<string>` for the
 * same reason as `HOOKED_COMMAND_TYPES` above.
 */
export const GENERIC_INTENT_TYPES: ReadonlySet<string> = new Set(
  Object.keys(GENERIC_INTENT_TYPE_RECORD) as GenericIntentType[],
);
